// Sesja konta w APP: przy starcie `GET /api/konto` (odnawia ciasteczko), potem logowanie / wylogowanie.
// Sama sesja żyje na zapleczu (ciasteczko HttpOnly) - telefon nie trzyma żetonu (S-9).
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import type { Account } from "./gen/kontrakt";
import { konto } from "./api/konto";
import { wyczyscPulpity } from "./api/pulpity";
import { wyczyscSerwis } from "./api/serwis";
import { wyczyscZaproszenia } from "./api/udostepnienia";
import { wyczyscOczekujace } from "./api/oczekujace";
import { wyczyscWnioski } from "./api/przypisanie";
import { wyczyscPlany } from "./api/plany";
import { wyczyscRMA } from "./api/rma";
import { dopasujJezyk } from "./i18n/t";
import { wyczyscCzat } from "./api/czat";
import { wyczyscNoweAlarmy } from "./api/powiadomienia";
import { BladZaplecza, ustawPrzyBrakuSesji } from "./api/zaplecze";
import { wyczyscKomendy } from "./brama/komendy";
import { wyczysc } from "./brama/magazyn";
import { zatrzymaj } from "./brama/polaczenie";
import { zapomnijPulpit } from "./ekrany/pulpity/ostatni";
import { wylaczBlokade } from "./uklad/blokada";
import { wyczyscOffline } from "./api/offline";

/** `brak` z `wygasla` - sesja skończyła się sama (wygasła, wylogowanie gdzie indziej), nie przyciskiem „Wyloguj”. */
export type StanSesji = { stan: "sprawdzam" } | { stan: "brak"; wygasla?: boolean } | { stan: "blad" } | { stan: "jest"; konto: Account };

interface Sesja {
  sesja: StanSesji;
  zalogowano: (k: Account) => void;
  wylogowano: (wygasla?: boolean) => void;
  sprawdz: () => void;
}

const Kontekst = createContext<Sesja | null>(null);

export function SesjaProvider({ children }: { children: ReactNode }) {
  const [sesja, ustaw] = useState<StanSesji>({ stan: "sprawdzam" });
  const idz = useNavigate();

  const pobierz = useCallback(() => {
    konto().then(
      (k) => { ustaw({ stan: "jest", konto: k }); dopasujJezyk(k.jezyk); },
      (b) => ustaw({ stan: b instanceof BladZaplecza && b.kod === "brak_sesji" ? "brak" : "blad" }),
    );
  }, []);
  const sprawdz = useCallback(() => {
    ustaw({ stan: "sprawdzam" });
    pobierz();
  }, [pobierz]);

  useEffect(pobierz, [pobierz]);

  const wartosc = useMemo<Sesja>(
    () => ({
      sesja,
      sprawdz,
      zalogowano: (k) => { ustaw({ stan: "jest", konto: k }); dopasujJezyk(k.jezyk); },
      wylogowano: (wygasla = false) => {
        ustawPrzyBrakuSesji(null);
        zatrzymaj();
        wyczysc();
        wyczyscKomendy();
        wyczyscPulpity();
        wyczyscZaproszenia();
        wyczyscSerwis();
        wyczyscOczekujace();
        wyczyscWnioski();
        wyczyscPlany();
        wyczyscRMA();
        wyczyscCzat();
        wyczyscNoweAlarmy();
        zapomnijPulpit();
        void wylaczBlokade(); // PIN aplikacji należy do tego konta - następne loguje się bez niego
        void wyczyscOffline(); // dane do pracy bez internetu (api/offline.ts) - też tylko tego konta
        // Adres poprzedniego konta (np. pulpit jego sztuki) nie zostaje dla następnego - start od `/`.
        void idz("/", { replace: true });
        ustaw({ stan: "brak", wygasla });
      },
    }),
    [sesja, sprawdz, idz],
  );
  return <Kontekst.Provider value={wartosc}>{children}</Kontekst.Provider>;
}

export function useSesja(): Sesja {
  const s = useContext(Kontekst);
  if (!s) throw new Error("useSesja poza SesjaProvider");
  return s;
}
